import axios from "axios";

function getApiBaseUrl() {
  return process.env.REACT_APP_API_URL || "";
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken");
  return token
    ? { accept: "*/*", Authorization: `Bearer ${token}` }
    : { accept: "*/*" };
}

const api = () => axios.create({ baseURL: getApiBaseUrl(), headers: getAuthHeaders() });

function crmRowId(row) {
  return Number(row?.id || row?.Id || 0) || 0;
}

export async function crmGetQueries(params) {
  const { data } = await api().get("Crm/Queries", { params });
  return { items: data.items || data.Items || [], totalCount: data.totalCount || data.TotalCount || 0 };
}

export async function crmGetQuery(id) {
  const { data } = await api().get(`Crm/Queries/${id}`);
  return data;
}

export async function crmSaveQuery(row) {
  const id = crmRowId(row);
  const payload = { ...row, id };
  if (id) {
    await api().put(`Crm/Queries/${id}`, payload);
    return id;
  }
  const { data } = await api().post("Crm/Queries", payload);
  return data.id || data.Id;
}

export async function crmDeleteQuery(id) {
  await api().delete(`Crm/Queries/${id}`);
}

export async function crmImportQueries(rows) {
  const { data } = await api().post("Crm/Queries/Import", rows);
  return data;
}

export async function crmConvertQuery(id) {
  const { data } = await api().post(`Crm/Queries/${id}/Convert`);
  return data.id || data.Id;
}

export async function crmLinkBooking(queryId, parcelId, invoiceNo) {
  const { data } = await api().post(`Crm/Queries/${queryId}/LinkBooking`, {
    parcelId,
    invoiceNo,
  });
  return data;
}

export async function crmGetFollowUps(queryId) {
  const { data } = await api().get(`Crm/Queries/${queryId}/FollowUps`);
  return Array.isArray(data) ? data : [];
}

export async function crmAddFollowUp(queryId, row) {
  const { data } = await api().post(`Crm/Queries/${queryId}/FollowUps`, row);
  return data.id || data.Id;
}

export async function crmGetDueDeliveries() {
  const { data } = await api().get("Crm/Orders/DueToday");
  return Array.isArray(data) ? data : [];
}

export async function crmMarkDelivered(id) {
  await api().post(`Crm/Orders/${id}/Delivered`);
}

export const CRM_LEAD_PREFILL_KEY = "crmLeadPrefill";

export function storeCrmLeadPrefill(lead) {
  sessionStorage.setItem(CRM_LEAD_PREFILL_KEY, JSON.stringify(lead || {}));
}

export function readCrmLeadPrefill() {
  try {
    const raw = sessionStorage.getItem(CRM_LEAD_PREFILL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearCrmLeadPrefill() {
  sessionStorage.removeItem(CRM_LEAD_PREFILL_KEY);
}

export async function crmGetOrders(params) {
  const { data } = await api().get("Crm/Orders", { params });
  return { items: data.items || data.Items || [], totalCount: data.totalCount || data.TotalCount || 0 };
}

export async function crmGetOrder(id) {
  const { data } = await api().get(`Crm/Orders/${id}`);
  return data;
}

export async function crmSaveOrder(row) {
  const id = crmRowId(row);
  const payload = { ...row, id };
  if (id) {
    await api().put(`Crm/Orders/${id}`, payload);
    return id;
  }
  const { data } = await api().post("Crm/Orders", payload);
  return data.id || data.Id;
}

export async function crmDeleteOrder(id) {
  await api().delete(`Crm/Orders/${id}`);
}

export async function crmImportOrders(rows) {
  const { data } = await api().post("Crm/Orders/Import", rows);
  return data;
}

export async function crmGetDashboard(params) {
  const { data } = await api().get("Crm/Dashboard", { params });
  return data;
}

export async function crmGetTasks(params) {
  const { data } = await api().get("Crm/Tasks", { params });
  return Array.isArray(data) ? data : [];
}

export async function crmGetTask(id) {
  const { data } = await api().get(`Crm/Tasks/${id}`);
  return data;
}

export async function crmSaveTask(row) {
  const id = crmRowId(row);
  const payload = { ...row, id };
  if (id) {
    await api().put(`Crm/Tasks/${id}`, payload);
    return id;
  }
  const { data } = await api().post("Crm/Tasks", payload);
  return data.id || data.Id;
}

export async function crmSaveSubtask(row) {
  const id = crmRowId(row);
  const payload = { ...row, id };
  if (id) {
    await api().put(`Crm/Subtasks/${id}`, payload);
    return id;
  }
  const { data } = await api().post("Crm/Subtasks", payload);
  return data.id || data.Id;
}

export async function crmDeleteSubtask(id) {
  await api().delete(`Crm/Subtasks/${id}`);
}

export async function crmGetComments(relatedType, relatedId) {
  const { data } = await api().get("Crm/Comments", { params: { relatedType, relatedId } });
  return Array.isArray(data) ? data : [];
}

export async function crmAddComment(row) {
  const { data } = await api().post("Crm/Comments", row);
  return data.id || data.Id;
}

export async function crmGetAttachments(relatedType, relatedId) {
  const { data } = await api().get("Crm/Attachments", { params: { relatedType, relatedId } });
  return Array.isArray(data) ? data : [];
}

export async function crmUpload(file, relatedType, relatedId) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api().post("Crm/Upload", form, {
    params: { relatedType, relatedId },
  });
  return data;
}

export async function crmFileBlobUrl(id) {
  const { data } = await api().get(`Crm/Files/${id}`, { responseType: "blob" });
  return URL.createObjectURL(data);
}

export function crmFileUrl(id) {
  return `${getApiBaseUrl()}Crm/Files/${id}`;
}

export async function crmAttendStart(relatedType, relatedId) {
  const { data } = await api().post("Crm/Attend/Start", { relatedType, relatedId });
  return data;
}

export async function crmAttendEnd(relatedType, relatedId) {
  await api().post("Crm/Attend/End", { relatedType, relatedId });
}

export async function crmGetAttend(relatedType, relatedId) {
  const { data } = await api().get("Crm/Attend", { params: { relatedType, relatedId } });
  return data;
}

export async function crmActivityLogin(screen, userName) {
  await api().post("Crm/Activity/Login", { screen, userName });
}

export async function crmActivityHeartbeat(screen, action, userName) {
  await api().post("Crm/Activity/Heartbeat", { screen, action, userName });
}

export async function crmActivityLogout() {
  try {
    await api().post("Crm/Activity/Logout");
  } catch {
    /* ignore */
  }
}

export async function crmGetSessions(params) {
  const { data } = await api().get("Crm/Activity/Sessions", { params });
  return Array.isArray(data) ? data : [];
}

export async function crmGetEvents(params) {
  const { data } = await api().get("Crm/Activity/Events", { params });
  return Array.isArray(data) ? data : [];
}

export async function crmGetOnline() {
  const { data } = await api().get("Crm/Activity/Online");
  return Array.isArray(data) ? data : [];
}

export async function crmGetStaff() {
  const { data } = await api().get("Crm/Staff");
  return Array.isArray(data) ? data : [];
}

export async function crmInboxStatus() {
  const { data } = await api().get("Crm/Inbox/Status");
  return data;
}

export function crmWhatsAppConnectUrl() {
  const base = getApiBaseUrl().replace(/\/?$/, "/");
  return `${base}WhatsApp/Connect`;
}

export async function crmInboxConversations(params) {
  const { data } = await api().get("Crm/Inbox/Conversations", { params });
  return {
    items: data.items || data.Items || [],
    totalCount: data.totalCount || data.TotalCount || 0,
  };
}

export async function crmInboxSync() {
  const { data } = await api().post("Crm/Inbox/Sync");
  return {
    items: data.items || data.Items || [],
    totalCount: data.totalCount || data.TotalCount || 0,
    sync: data.sync || data.Sync || null,
  };
}

export async function crmInboxMessages(id, take = 100) {
  const { data } = await api().get(`Crm/Inbox/Conversations/${id}/Messages`, {
    params: { take },
  });
  return Array.isArray(data) ? data : [];
}

export async function crmInboxSend(id, body) {
  const { data } = await api().post(`Crm/Inbox/Conversations/${id}/Messages`, { body });
  return Array.isArray(data) ? data : [];
}

export async function crmInboxSendMedia(id, file, caption) {
  const form = new FormData();
  form.append("file", file);
  if (caption) form.append("caption", caption);
  const { data } = await api().post(`Crm/Inbox/Conversations/${id}/Media`, form);
  return Array.isArray(data) ? data : [];
}

function crmAuthToken() {
  return (
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken") ||
    ""
  );
}

/** Stream URL for &lt;video&gt;/&lt;audio&gt;/&lt;img&gt; — uses access_token query (no full blob download first). */
export function crmInboxMediaStreamUrl(conversationId, messageId) {
  const base = getApiBaseUrl().replace(/\/?$/, "/");
  const token = crmAuthToken();
  const q = token ? `?access_token=${encodeURIComponent(token)}` : "";
  return `${base}Crm/Inbox/Conversations/${conversationId}/Messages/${messageId}/File${q}`;
}

export async function crmInboxMediaBlobUrl(conversationId, messageId, messageType) {
  const response = await api().get(
    `Crm/Inbox/Conversations/${conversationId}/Messages/${messageId}/File`,
    { responseType: "blob" },
  );
  const data = response?.data;
  if (!data) throw new Error("Empty media response");

  const headerType = String(
    response?.headers?.["content-type"] || response?.headers?.["Content-Type"] || "",
  )
    .split(";")[0]
    .trim()
    .toLowerCase();
  const kind = String(messageType || "").toLowerCase();
  let type = (data.type || headerType || "").split(";")[0].trim().toLowerCase();
  if (
    (!type || type === "application/octet-stream") &&
    ["audio", "voice", "ptt"].includes(kind)
  ) {
    type = "audio/ogg";
  } else if (
    (!type || type === "application/octet-stream") &&
    ["image", "sticker"].includes(kind)
  ) {
    type = "image/jpeg";
  } else if ((!type || type === "application/octet-stream") && kind === "video") {
    type = "video/mp4";
  }

  const blob =
    type && data.type !== type ? new Blob([data], { type }) : data;
  return URL.createObjectURL(blob);
}

export async function crmInboxSendTest(to, body) {
  const { data } = await api().post("Crm/Inbox/SendTest", { to, body });
  return data;
}

export function crmErrorText(err) {
  const data = err?.response?.data;
  const raw =
    (typeof data === "string" && data.trim() && data)
    || data?.message
    || data?.Message
    || data?.title
    || data?.Title
    || err?.message
    || "Could not send.";
  const text = String(raw);
  if (text.includes("131005") || text.includes("problem with the access token")) {
    return "This Meta token can manage the test number but Meta blocked send. On WAN8n → WhatsApp → API Setup click Generate token on that page, paste it, Save token to CRM, then send again. A Graph Explorer or Facebook Login user token will not send.";
  }
  if (
    text.includes("error_subcode\":33")
    || text.includes("does not exist")
    || text.includes("missing permissions")
    || text.includes("\"code\":190")
  ) {
    return "CRM already has a Meta token saved. Meta still rejected send on this phone, so that saved token is expired, revoked, or from a different app. Generate a new token on WAN8n → WhatsApp → Step 1, paste it, Save token to CRM, then send again.";
  }
  return text;
}

export async function crmInboxPause(id, botPaused) {
  const { data } = await api().patch(`Crm/Inbox/Conversations/${id}/Pause`, { botPaused });
  return data;
}

export async function crmInboxBotSettings(payload) {
  const { data } = await api().patch("Crm/Inbox/BotSettings", payload);
  return data;
}

export async function crmInboxCredentials(payload) {
  const { data } = await api().post("Crm/Inbox/Credentials", payload);
  return data;
}


export function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  const src = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => String(c || "").trim()));
}

function normHeader(h) {
  return String(h || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function pick(map, row, keys) {
  for (const key of keys) {
    if (row[key] != null && String(row[key]).trim() !== "") return row[key];
  }
  return "";
}

function num(v) {
  const n = parseFloat(String(v || "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function dateVal(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function mapQueryCsv(text) {
  const table = parseCsv(text);
  if (table.length < 2) return [];
  const headers = table[0].map(normHeader);
  return table.slice(1).map((cells) => {
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cells[i];
    });
    return {
      queryDate: dateVal(pick(null, row, ["date", "querydate"])),
      customerName: pick(null, row, ["customername", "customer", "name"]),
      phone: pick(null, row, ["phone", "phonenumber", "mobile"]),
      address: pick(null, row, ["address"]),
      productType: pick(null, row, ["producttype", "product"]),
      email: pick(null, row, ["email", "mail"]),
      weightKg: num(pick(null, row, ["weightkg", "weight"])),
      cost: num(pick(null, row, ["costperkgtotalcost", "cost", "totalcost"])),
      boxCount: num(pick(null, row, ["nofbox", "box", "boxes", "boxcount"])),
      source: pick(null, row, ["source"]),
      ownerName: pick(null, row, ["supportexecutive", "owner", "handledby"]),
      route: pick(null, row, ["route"]),
      stage: pick(null, row, ["stage"]) || "New Lead",
      calledDate: dateVal(pick(null, row, ["calleddate"])),
      confirmByName: pick(null, row, ["confirmby", "confirmbyname"]),
      cellStatus: pick(null, row, ["cellstatus"]),
      invoiceNo: pick(null, row, ["consignmentinvoice", "invoice", "invoiceno"]),
      paymentStatus: pick(null, row, ["paymentstatus"]),
      amount: num(pick(null, row, ["amount", "amount"])),
      receiveSerial: pick(null, row, ["receiveserial"]),
      phBookingAgent: pick(null, row, ["phbookingagent"]),
      dropDate: dateVal(pick(null, row, ["dropdate"])),
      deliveryDate: dateVal(pick(null, row, ["deliverydate", "deliverydate"])),
      status: pick(null, row, ["status"]),
      bookingStatus: pick(null, row, ["bookingstatus"]),
      trackingLink: pick(null, row, ["phtracking", "tracking", "trackinglink"]),
      notes: pick(null, row, ["notes"]),
    };
  });
}

export function mapOrderCsv(text) {
  const table = parseCsv(text);
  if (table.length < 2) return [];
  const headers = table[0].map(normHeader);
  return table.slice(1).map((cells) => {
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cells[i];
    });
    return {
      invoiceNo: pick(null, row, ["bookingidinvoiceno", "invoiceno", "bookingid", "invoice"]),
      invoiceFileUrl: pick(null, row, ["invoicefilelink", "invoicefileurl"]),
      monthName: pick(null, row, ["month"]),
      bookingDate: dateVal(pick(null, row, ["bookinginvdate", "bookingdate", "date"])),
      route: pick(null, row, ["route"]),
      handledByName: pick(null, row, ["handledby"]),
      source: pick(null, row, ["platformsource", "source"]),
      senderName: pick(null, row, ["customersendername", "sendername", "customer"]),
      senderPhone: pick(null, row, ["senderphonewhatsapp", "senderphone", "phone"]),
      senderEmail: pick(null, row, ["senderemail", "email"]),
      pickupAddress: pick(null, row, ["pickupaddress"]),
      receiverName: pick(null, row, ["receivername"]),
      receiverEmail: pick(null, row, ["receiveremail"]),
      receiverPhone: pick(null, row, ["phonenumber", "receiverphone"]),
      deliveryAddress: pick(null, row, ["deliveryaddress"]),
      itemDescription: pick(null, row, ["itemdescription", "item"]),
      boxQty: num(pick(null, row, ["boxqty"])),
      weightQty: num(pick(null, row, ["weightqty", "weight"])),
      dimensionalWeight: num(pick(null, row, ["dimensionalweight"])),
      weightSlab: pick(null, row, ["weightslab"]),
      carrierCost: num(pick(null, row, ["carriercost"])),
      priceCharged: num(pick(null, row, ["pricecharged", "price"])),
      paymentNotes: pick(null, row, ["notesregardingpayments", "paymentnotes"]),
      paymentReceiptUrl: pick(null, row, ["paymentreceiptlink"]),
      refundAmount: num(pick(null, row, ["refundamount"])),
      deductedAmount: num(pick(null, row, ["deductedamount"])),
      refundStatus: pick(null, row, ["refundstatus"]),
      paymentMethod: pick(null, row, ["paymentmethod"]),
      paymentStatus: pick(null, row, ["paymentstatus"]),
      paymentReceivedDate: dateVal(pick(null, row, ["paymentreceiveddate"])),
      trackingNumber: pick(null, row, ["trackingnumber"]),
      trackingLink: pick(null, row, ["trackinglink"]),
      ukLastStatus: pick(null, row, ["ukofficedeliverylaststatus", "uklaststatus"]),
      bdDeliveryDate: dateVal(pick(null, row, ["bddeliverydate"])),
      deliveryLastStatus: pick(null, row, ["deliverylaststatus"]),
      packingList: pick(null, row, ["packinglist"]),
      airwayBillNo: pick(null, row, ["airwaybillno"]),
      airwayBillLink: pick(null, row, ["airwaybilllinkfromdrive", "airwaybilllink"]),
    };
  });
}

export function waMeUrl(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  return `https://wa.me/${digits}`;
}
